import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PublicKey } from '@solana/web3.js';

import { useWalletAddress } from './useWalletAddress';

import { getConnection } from '@/lib/solana/anchorClient';
import { getUserInfoAccount, getGlobalConfigAccount } from '@/lib/solana/accounts';
import type { UserProgress } from '@/types/schemas';

// Extended user progress that includes chest credit information
export interface ExtendedUserProgress extends UserProgress {
  chest_credits: string; // TOTAL credits for opening chests (burning + deposits combined)
  chest_price: string; // Price per chest in lamports
  available_chests: number; // Number of chests user can open with current credits
  remainder_credits: string; // Credits left over after max chests (not enough for another chest)
}

async function fetchUserProgress(address: string | undefined): Promise<ExtendedUserProgress | null> {
  if (!address) {
    return null;
  }

  // TEMPORARY: Skip backend API and use direct blockchain query until backend implements /user/progress
  // TODO: Re-enable backend API once endpoint is implemented
  try {
    const connection = getConnection();
    const walletPubkey = new PublicKey(address);

    // Fetch user info and global config in parallel
    const [userInfo, globalConfig] = await Promise.all([
      getUserInfoAccount(connection, walletPubkey),
      getGlobalConfigAccount(connection),
    ]);

    if (!globalConfig) {
      console.error('GlobalConfig not found - program may not be initialized');
      return null;
    }

    // Map program data to frontend schema
    // IMPORTANT: UserInfo.balance is the TOTAL credits (burning + deposits)
    // This is what the program uses to determine how many chests can be opened

    // Get user's TOTAL chest credits (burning + deposits combined)
    // This is what determines how many chests they can open AND what they can withdraw
    const chestCredits = userInfo?.balance || BigInt(0);
    const chestPrice = globalConfig.chestPrice;

    // Calculate how many chests the user can open with their credits
    const availableChests = chestPrice > BigInt(0)
      ? Number(chestCredits / chestPrice)
      : 0;

    // Calculate remainder credits (credits left over that can't buy another chest)
    const remainderCredits = chestPrice > BigInt(0)
      ? chestCredits % chestPrice
      : chestCredits;

    return {
      burns_since_last_spin: 0, // Program doesn't track burns, legacy field
      can_jackpot: false, // Legacy jackpot mechanic, not in program
      sol_balance: chestCredits.toString(), // User's total credits (same as chest_credits)
      remainder_sol: '0', // Not tracked in program
      total_contributed_sol: '0', // Not tracked in program
      chest_credits: chestCredits.toString(), // User's credits for opening chests
      chest_price: chestPrice.toString(), // Price per chest in lamports
      available_chests: availableChests, // Number of chests user can open
      remainder_credits: remainderCredits.toString(), // Credits left over after max chests
    };
  } catch (error) {
    console.error('Error fetching user progress from blockchain:', error);
    return null;
  }
}

export function useUserProgress() {
  const { address } = useWalletAddress();
  const queryClient = useQueryClient();

  const {
    data: userProgress,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['userProgress', address],
    queryFn: () => fetchUserProgress(address),
    enabled: !!address,
    staleTime: 0, // Always fetch fresh data to show SOL rewards immediately
    refetchOnWindowFocus: true,
    refetchOnMount: true, // Always refetch when component mounts
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['userProgress', address] });
  };

  return {
    userProgress,
    isLoading,
    error,
    refetch,
    invalidate,
  };
}
